import { useState, type CSSProperties, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Icon } from "@majourneys/icons";
import { PW, pewterPanelApproved } from "../internal-ui/pewter-shell";

export type TicketStatus = "pending" | "confirmed" | "completed" | "cancelled" | "rejected";

export interface BookingTicketProps {
  image?: string | null;
  title: string;
  /** One line above the title: party and city ("Table for 2 · Casablanca"). */
  subtitle?: string;
  status: TicketStatus;
  /** ISO date (YYYY-MM-DD) of the visit, or the check-in for a stay. */
  date: string;
  /** "HH:mm", or a flexible window "13:00–15:00". Omitted for stays and untimed bookings. */
  time?: string | null;
  /** Stays: shown in the time column instead of a time ("2 nights"). */
  nightsLabel?: string | null;
  /** Short reference printed on the ticket's foot, with a copy button. */
  reference?: string | null;
  /** How the reference is drawn (the sheet types it out); defaults to plain text. */
  renderReference?: (reference: string) => ReactNode;
  /**
   * Torn-off part under the perforation. The host app passes its account
   * box here (it draws its own `TicketPerforation`), or nothing for a guest
   * who is already signed in, and the ticket simply ends.
   */
  stub?: ReactNode;
  /** Colour behind the ticket, so the two notches read as holes. */
  notchColor?: string;
}

const STATUS_STYLE: Record<TicketStatus, { color: string; bg: string; dot: string }> = {
  pending: { color: "#8a5e15", bg: "rgba(255,255,255,0.92)", dot: PW.gold },
  confirmed: { color: PW.green, bg: "rgba(255,255,255,0.92)", dot: "#1fa855" },
  completed: { color: PW.green, bg: "rgba(255,255,255,0.92)", dot: "#1fa855" },
  cancelled: { color: PW.red, bg: "rgba(255,255,255,0.92)", dot: "#dc2626" },
  rejected: { color: PW.red, bg: "rgba(255,255,255,0.92)", dot: "#dc2626" },
};

/**
 * Weekday and day-month for the ticket's big date. Parsed and formatted in
 * UTC so the server and the browser print the same day (a host-clock date
 * here is a hydration mismatch). Latin digits in every locale, ar included.
 */
function ticketDate(iso: string, lang: string): { weekday: string; day: string } {
  const [y, m, d] = iso.split("-").map(Number);
  if (!y || !m || !d) return { weekday: "", day: iso };
  const date = new Date(Date.UTC(y, m - 1, d));
  const locale = `${lang}-u-nu-latn`;
  return {
    weekday: new Intl.DateTimeFormat(locale, { weekday: "long", timeZone: "UTC" }).format(date),
    day: new Intl.DateTimeFormat(locale, { day: "numeric", month: "short", timeZone: "UTC" }).format(date),
  };
}

export function BookingTicket({
  image,
  title,
  subtitle,
  status,
  date,
  time,
  nightsLabel,
  reference,
  renderReference,
  stub,
  notchColor = "#FAFAFC",
}: BookingTicketProps) {
  const { t, i18n } = useTranslation();
  const [copied, setCopied] = useState(false);
  const { weekday, day } = ticketDate(date, i18n.language || "en");
  const st = STATUS_STYLE[status];
  const statusLabel =
    status === "confirmed" || status === "completed"
      ? t("activity.confirmed", { defaultValue: "Confirmed" })
      : status === "cancelled"
        ? t("activity.cancelled", { defaultValue: "Cancelled" })
        : status === "rejected"
          ? t("activity.rejected", { defaultValue: "Rejected" })
          : t("activity.awaiting_confirmation", { defaultValue: "Awaiting confirmation" });

  return (
    <div
      style={{ ...pewterPanelApproved(26), ["--ticket-notch" as string]: notchColor } as CSSProperties}
      data-testid="booking-ticket"
    >
      {/* Clips the tear line's notches into half circles. */}
      <div className="overflow-hidden rounded-[22px]">
        <div
          className="relative h-[140px] bg-cover bg-center"
          style={
            image
              ? { backgroundImage: `url(${image})` }
              : { background: "linear-gradient(135deg, rgba(229,178,101,0.35), rgba(125,108,245,0.25))" }
          }
        >
          <div className="absolute inset-0 bg-gradient-to-t from-[#110E39]/45 to-transparent" />
          <span
            className="absolute start-3 top-3 inline-flex items-center gap-1.5 rounded-full px-2.5 py-1"
            style={{ background: st.bg }}
          >
            <span className="h-2 w-2 rounded-full" style={{ background: st.dot }} />
            <span
              className="font-sans text-[10.5px] font-bold uppercase tracking-[0.1em]"
              style={{ color: st.color }}
            >
              {statusLabel}
            </span>
          </span>
        </div>

        <div className="px-5 pb-4 pt-4">
          {subtitle ? (
            <p className="font-sans text-[12.5px] font-semibold text-[#5a5d77]">{subtitle}</p>
          ) : null}
          <p className="mt-0.5 font-heading text-[22px] font-bold leading-tight text-[#110E39]">
            {title}
          </p>
          <div className="mt-4 flex flex-wrap gap-x-6 gap-y-2">
            <div className="min-w-0">
              <p className="font-sans text-[10.5px] font-bold uppercase tracking-[0.1em] text-[#5a5d77]">
                {weekday}
              </p>
              <p className="font-heading text-[26px] font-bold leading-none text-[#110E39]">{day}</p>
            </div>
            {time || nightsLabel ? (
              <div className="min-w-0">
                <p className="font-sans text-[10.5px] font-bold uppercase tracking-[0.1em] text-[#5a5d77]">
                  {time
                    ? t("activity.time", { defaultValue: "Time" })
                    : t("ticket.stay", { defaultValue: "Stay" })}
                </p>
                <p className="font-heading text-[26px] font-bold leading-none text-[#110E39]" dir="ltr">
                  {time ? (time.includes("–") ? time : time.slice(0, 5)) : nightsLabel}
                </p>
              </div>
            ) : null}
          </div>
          {status === "cancelled" || status === "rejected" ? null : <TicketProgress status={status} />}
        </div>

        {stub}

        {reference ? (
          <div
            className="flex items-center justify-between gap-3 border-t px-5 py-2"
            style={{ borderColor: PW.hairline }}
          >
            <span className="font-mono text-[11.5px] font-bold text-[#5a5d77]" dir="ltr">
              {renderReference ? renderReference(reference) : reference}
            </span>
            <button
              type="button"
              onClick={async () => {
                try {
                  await navigator.clipboard.writeText(reference);
                  setCopied(true);
                  setTimeout(() => setCopied(false), 1800);
                } catch {
                  /* clipboard may be blocked */
                }
              }}
              className="inline-flex min-h-[32px] items-center gap-1 rounded-full px-2 font-sans text-[11.5px] font-bold text-[#110E39]"
            >
              <Icon name={copied ? "ri-check-line" : "ri-file-copy-line"} className="text-[14px]" aria-hidden />
              {copied
                ? t("common.copied", { defaultValue: "Copied" })
                : t("activity.copy_reference", { defaultValue: "Copy" })}
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );
}

function TicketProgress({ status }: { status: TicketStatus }) {
  const { t } = useTranslation();
  const done = status === "confirmed" || status === "completed";
  const steps = [
    t("ticket.step_booked", { defaultValue: "Booked" }),
    t("ticket.step_confirming", { defaultValue: "Confirming" }),
    t("ticket.step_confirmed", { defaultValue: "Confirmed" }),
  ];
  return (
    <div className="mt-5" aria-label={steps[done ? 2 : 1]}>
      <div className="flex items-center gap-1.5" aria-hidden>
        <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: PW.green }} />
        <span className="h-[3px] flex-1 rounded-full" style={{ background: PW.green }} />
        <span
          className="relative h-2.5 w-2.5 shrink-0 rounded-full"
          style={{ background: done ? PW.green : PW.gold }}
        >
          {!done ? (
            <span
              className="absolute inset-0 animate-ping rounded-full motion-reduce:hidden"
              style={{ background: PW.gold, opacity: 0.5 }}
            />
          ) : null}
        </span>
        <span
          className="h-[3px] flex-1 rounded-full"
          style={{ background: done ? PW.green : "rgba(17,14,57,0.1)" }}
        />
        <span
          className="h-2.5 w-2.5 shrink-0 rounded-full"
          style={{ background: done ? PW.green : "rgba(17,14,57,0.15)" }}
        />
      </div>
      <div className="mt-1.5 flex justify-between gap-2 font-sans text-[11px] font-semibold text-[#5a5d77]">
        {steps.map((s, i) => (
          <span key={s} style={i === 1 && !done ? { color: "#8a5e15" } : undefined}>
            {s}
          </span>
        ))}
      </div>
    </div>
  );
}

/**
 * The dashed tear line with a notch on each side, drawn exactly like the
 * tear line on the thank-you pass (pass-thank-you.tsx). The notches are
 * circles in the colour behind the ticket (`--ticket-notch`), cut in half by
 * the ticket's own clipping, so the parent must clip (overflow hidden). The
 * host app's stub draws it on top of itself, so a ticket with no stub ends
 * cleanly.
 */
export function TicketPerforation() {
  const notch: CSSProperties = { background: "var(--ticket-notch, #FAFAFC)" };
  return (
    <div className="relative h-6" aria-hidden>
      <span className="absolute -start-3 top-0 h-6 w-6 rounded-full" style={notch} />
      <span className="absolute -end-3 top-0 h-6 w-6 rounded-full" style={notch} />
      <span
        className="absolute start-5 end-5 top-1/2 border-t-2 border-dashed"
        style={{ borderColor: PW.hairline }}
      />
    </div>
  );
}

/** Maps the API's booking status onto the ticket's five looks. */
export function ticketStatusOf(status: string): TicketStatus {
  switch (status) {
    case "CONFIRMED":
    case "PAID":
      return "confirmed";
    case "COMPLETED":
      return "completed";
    case "CANCELLED":
      return "cancelled";
    case "REJECTED":
      return "rejected";
    default:
      return "pending";
  }
}
